import { type BalanceSheetId, type FinancialPeriodId, type IncomeStatementId } from "@inrev/common";
import {
	createContext,
	type Dispatch,
	type FC,
	type ReactNode,
	type SetStateAction,
	useContext,
	useMemo,
} from "react";
import { usePeriodSelection } from "../patterns/period-selector";
import type {
	ActiveStatementType,
	AnalysisPeriodSelectorItem,
	BalanceSheetPeriodSelectorItem,
	IncomeStatementPeriodSelectorItem,
	PeriodSelectorItem,
} from "./types";
import {
	computeDefaultAnalysisPeriodSelectorIds,
	computeDefaultBalanceSheetPeriodSelectorIds,
	computeDefaultIncomeStatementPeriodSelectorIds,
} from "./utils/computeDefaultPeriodSelectorIds";

export type {
	ActiveStatementType,
	AnalysisPeriodSelectorItem,
	BalanceSheetPeriodSelectorItem,
	IncomeStatementPeriodSelectorItem,
	PeriodSelectorItem,
} from "./types";

type FinancialsPeriodSelectorContextValue = {
	activeStatementType: ActiveStatementType;
	periodSelectorItems: PeriodSelectorItem[];
	selectedBalanceSheetIds: Set<BalanceSheetId>;
	setSelectedBalanceSheetIds: Dispatch<SetStateAction<Set<BalanceSheetId>>>;
	selectedIncomeStatementIds: Set<IncomeStatementId>;
	setSelectedIncomeStatementIds: Dispatch<SetStateAction<Set<IncomeStatementId>>>;
	selectedFinancialPeriodIds: Set<FinancialPeriodId>;
	setSelectedFinancialPeriodIds: Dispatch<SetStateAction<Set<FinancialPeriodId>>>;
	toggleBalanceSheet: (id: BalanceSheetId) => void;
	toggleIncomeStatement: (id: IncomeStatementId) => void;
	toggleFinancialPeriod: (id: FinancialPeriodId) => void;
	isBalanceSheetSelected: (id: BalanceSheetId) => boolean;
	isIncomeStatementSelected: (id: IncomeStatementId) => boolean;
	isFinancialPeriodSelected: (id: FinancialPeriodId) => boolean;
	selectedCountForTab: number;
};

const FinancialsPeriodSelectorContext = createContext<FinancialsPeriodSelectorContextValue | null>(
	null,
);

export const useFinancialsPeriodSelector = (): FinancialsPeriodSelectorContextValue => {
	const ctx = useContext(FinancialsPeriodSelectorContext);
	if (!ctx)
		throw new Error(
			"useFinancialsPeriodSelector must be used inside FinancialsPeriodSelectorProvider",
		);
	return ctx;
};

type FinancialsPeriodSelectorProviderProps = {
	balanceSheetPeriodSelectorItems: BalanceSheetPeriodSelectorItem[];
	incomeStatementPeriodSelectorItems: IncomeStatementPeriodSelectorItem[];
	analysisPeriodSelectorItems: AnalysisPeriodSelectorItem[];
	activeStatementType: ActiveStatementType;
	children: ReactNode;
};

export const FinancialsPeriodSelectorProvider: FC<FinancialsPeriodSelectorProviderProps> = ({
	balanceSheetPeriodSelectorItems,
	incomeStatementPeriodSelectorItems,
	analysisPeriodSelectorItems,
	activeStatementType,
	children,
}) => {
	const balanceSheetItemIds = useMemo(
		() => balanceSheetPeriodSelectorItems.map((item) => item.id),
		[balanceSheetPeriodSelectorItems],
	);
	const defaultBalanceSheetIds = useMemo(
		() => computeDefaultBalanceSheetPeriodSelectorIds(balanceSheetPeriodSelectorItems),
		[balanceSheetPeriodSelectorItems],
	);
	const incomeStatementItemIds = useMemo(
		() => incomeStatementPeriodSelectorItems.map((item) => item.id),
		[incomeStatementPeriodSelectorItems],
	);
	const defaultIncomeStatementIds = useMemo(
		() => computeDefaultIncomeStatementPeriodSelectorIds(incomeStatementPeriodSelectorItems),
		[incomeStatementPeriodSelectorItems],
	);
	const analysisItemIds = useMemo(
		() => analysisPeriodSelectorItems.map((item) => item.id),
		[analysisPeriodSelectorItems],
	);
	const defaultAnalysisIds = useMemo(
		() => computeDefaultAnalysisPeriodSelectorIds(analysisPeriodSelectorItems),
		[analysisPeriodSelectorItems],
	);

	const balanceSheets = usePeriodSelection(balanceSheetItemIds, defaultBalanceSheetIds);
	const incomeStatements = usePeriodSelection(incomeStatementItemIds, defaultIncomeStatementIds);
	const analysisPeriods = usePeriodSelection(analysisItemIds, defaultAnalysisIds);

	const periodSelectorItems = useMemo((): PeriodSelectorItem[] => {
		if (activeStatementType === "balance_sheet") return balanceSheetPeriodSelectorItems;
		if (activeStatementType === "income_statement") return incomeStatementPeriodSelectorItems;
		if (activeStatementType === "analysis") return analysisPeriodSelectorItems;
		return [];
	}, [
		activeStatementType,
		balanceSheetPeriodSelectorItems,
		incomeStatementPeriodSelectorItems,
		analysisPeriodSelectorItems,
	]);

	const selectedCountForTab =
		activeStatementType === "balance_sheet"
			? balanceSheets.selectedCount
			: activeStatementType === "income_statement"
				? incomeStatements.selectedCount
				: activeStatementType === "analysis"
					? analysisPeriods.selectedCount
					: 0;

	const value = useMemo(
		() => ({
			activeStatementType,
			periodSelectorItems,
			selectedBalanceSheetIds: balanceSheets.selectedIds,
			setSelectedBalanceSheetIds: balanceSheets.setSelectedIds,
			selectedIncomeStatementIds: incomeStatements.selectedIds,
			setSelectedIncomeStatementIds: incomeStatements.setSelectedIds,
			selectedFinancialPeriodIds: analysisPeriods.selectedIds,
			setSelectedFinancialPeriodIds: analysisPeriods.setSelectedIds,
			toggleBalanceSheet: balanceSheets.toggle,
			toggleIncomeStatement: incomeStatements.toggle,
			toggleFinancialPeriod: analysisPeriods.toggle,
			isBalanceSheetSelected: balanceSheets.isSelected,
			isIncomeStatementSelected: incomeStatements.isSelected,
			isFinancialPeriodSelected: analysisPeriods.isSelected,
			selectedCountForTab,
		}),
		[
			activeStatementType,
			periodSelectorItems,
			balanceSheets.selectedIds,
			balanceSheets.setSelectedIds,
			balanceSheets.toggle,
			balanceSheets.isSelected,
			incomeStatements.selectedIds,
			incomeStatements.setSelectedIds,
			incomeStatements.toggle,
			incomeStatements.isSelected,
			analysisPeriods.selectedIds,
			analysisPeriods.setSelectedIds,
			analysisPeriods.toggle,
			analysisPeriods.isSelected,
			selectedCountForTab,
		],
	);

	return (
		<FinancialsPeriodSelectorContext.Provider value={value}>
			{children}
		</FinancialsPeriodSelectorContext.Provider>
	);
};
